import {
  Component,
  OnInit,
  Renderer2,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { PercentPipe } from '@angular/common';
import {
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Utilities } from '@approot/utilities';
import { Entity, CreditCurve, KeyValue, RequestStatus } from '@approot/models';
import { LegService, TradesCaptureService } from '@approot/services';

@Component({
  selector: 'app-credit-curves',
  templateUrl: './credit-curves.component.html',
  styleUrls: ['./credit-curves.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditCurvesComponent implements OnInit {
  public creditCurvesForm: FormGroup;
  public selectedCurveIndex: number = null;
  public selectedTenorIndex: number = null;
  public savingCurvesStatus: RequestStatus = '';
  public selectedPercentField = null;
  public loadingCurves = true;
  public identifiers: string[] = [];
  @ViewChild('divToScrollCurves', { static: false })
  public divToScrollCurves: ElementRef;
  @ViewChild('divToScrollTenors', { static: false })
  public divToScrollTenors: ElementRef;
  private curvesToDelete = [];
  constructor(
    private renderer: Renderer2,
    private tradesCaptureService: TradesCaptureService,
    private percentPipe: PercentPipe,
    private legService: LegService,
    private changeRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.creditCurvesForm = new FormGroup({
      curves: new FormArray([])
    });

    let requests = {};

    requests['entities'] = this.tradesCaptureService.getEntities();

    requests['creditCurves'] = this.tradesCaptureService.getUserInfoRequest({
      task: 'getAll',
      table: 'creditCurves'
    });

    forkJoin(requests).subscribe({
      next: (response: { entities: Entity[]; creditCurves: CreditCurve[] }) => {
        const entities = response.entities;
        const creditCurves = response.creditCurves;
        this.updateLocalEntities(entities);

        creditCurves.forEach(curve => {
          if (!this.identifiers.includes(curve.entity)) {
            // hide entities which no longer exist
            return;
          }
          const createdForm = this.creditCurveFormMaker(curve);
          (this.creditCurvesForm.get('curves') as FormArray).push(createdForm);
        });
        this.loadingCurves = false;
        this.changeRef.markForCheck();
      }
    });
  }

  creditCurveFormMaker(curve: CreditCurve): FormGroup {
    const spreads = new FormArray([], Validators.required);
    const tenors = new FormArray([], Validators.required);

    if (curve && curve.spreads) {
      curve.spreads.forEach(spread => {
        spreads.push(this.addNewSpread(spread));
      });
    }

    if (curve && curve.tenors) {
      curve.tenors.forEach(tenor => {
        tenors.push(this.addNewTenor(tenor));
      });
    }

    const controlGroup = new FormGroup({
      cdsRecoveryRate: new FormControl(
        curve && curve.cdsRecoveryRate
          ? this.percentPipe.transform(curve.cdsRecoveryRate, '1.0-12')
          : '40%',
        [Validators.required, this.percentRateValidator.bind(this)]
      ),
      currency: new FormControl(
        curve && curve.currency ? curve.currency : '',
        Validators.required
      ),
      derivativeRecoveryRate: new FormControl(
        curve && curve.derivativeRecoveryRate
          ? this.percentPipe.transform(curve.derivativeRecoveryRate, '1.0-12')
          : '25%',
        [Validators.required, this.percentRateValidator.bind(this)]
      ),
      entity: new FormControl(curve && curve.entity ? curve.entity : '', [
        Validators.required
      ]),
      spreadDayCountConvention: new FormControl(
        curve && curve.spreadDayCountConvention
          ? curve.spreadDayCountConvention
          : 'Act/360',
        [Validators.required]
      ),
      spreadFrequency: new FormControl(
        curve && curve.spreadFrequency ? curve.spreadFrequency : '3m',
        [Validators.required]
      ),
      spreads: spreads,
      tenors: tenors,
      saved: new FormControl({
        value: !!curve,
        disabled: true
      })
    });

    controlGroup.get('derivativeRecoveryRate').valueChanges.subscribe(val => {
      Utilities.endOfTextBoxAdder(
        this.selectedPercentField,
        controlGroup.get('derivativeRecoveryRate'),
        '%',
        !!val,
        'percentage',
        this.renderer
      );
    });

    controlGroup.get('cdsRecoveryRate').valueChanges.subscribe(val => {
      Utilities.endOfTextBoxAdder(
        this.selectedPercentField,
        controlGroup.get('cdsRecoveryRate'),
        '%',
        !!val,
        'percentage',
        this.renderer
      );
    });

    return controlGroup;
  }

  textBoxCaretRestrictionHandler(event, action: string) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(event.target.value, '%');
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  deleteTenor() {
    (this.tenorSpreadArray('spreads') as FormArray).removeAt(
      this.selectedTenorIndex
    );
    (this.tenorSpreadArray('spreads') as FormArray).markAsDirty();

    (this.tenorSpreadArray('tenors') as FormArray).removeAt(
      this.selectedTenorIndex
    );
    (this.tenorSpreadArray('tenors') as FormArray).markAsDirty();
    this.selectedTenorIndex = null;
  }

  deleteCurve() {
    const curveToDelete = this.curvesArray.controls[
      this.selectedCurveIndex
    ] as FormGroup;

    if (curveToDelete.get('saved').value) {
      this.curvesToDelete.push(curveToDelete.value);
    }

    this.curvesArray.removeAt(this.selectedCurveIndex);
    this.selectedCurveIndex = null;
  }

  get curvesArray() {
    return this.creditCurvesForm.get('curves') as FormArray;
  }

  tenorSpreadArray(arrayName: string) {
    if (this.selectedCurveIndex !== null) {
      // if there is curve selected, bet the spreads and tenors array of that curve
      return (this.creditCurvesForm.get('curves') as FormArray).controls[
        this.selectedCurveIndex
      ].get(arrayName) as FormArray;
    }
    return [];
  }

  addNewCurve() {
    (this.creditCurvesForm.get('curves') as FormArray).push(
      this.creditCurveFormMaker(null)
    );
    this.updateScroll('curves');
  }

  addNewSpreadTenor() {
    if (this.selectedCurveIndex !== null) {
      (
        (
          (this.creditCurvesForm.get('curves') as FormArray).controls[
            this.selectedCurveIndex
          ] as FormGroup
        ).get('spreads') as FormArray
      ).push(this.addNewSpread(''));

      (
        (
          (this.creditCurvesForm.get('curves') as FormArray).controls[
            this.selectedCurveIndex
          ] as FormGroup
        ).get('tenors') as FormArray
      ).push(this.addNewTenor(''));
    }
    this.updateScroll('tenors');
  }

  addNewSpread(spread) {
    const control = new FormControl(
      !isNaN(spread) && spread !== null && spread !== ''
        ? this.percentPipe.transform(spread, '1.0-12')
        : '',
      [Validators.required, this.spreadValidator.bind(this)]
    );

    control.valueChanges.subscribe(val => {
      Utilities.endOfTextBoxAdder(
        this.selectedPercentField,
        control,
        '%',
        !!val,
        'percentage',
        this.renderer
      );
    });

    return control;
  }

  addNewTenor(tenor) {
    return new FormControl(tenor, [Validators.required]);
  }

  saveChanges() {
    const changedRows = (
      this.creditCurvesForm.get('curves') as FormArray
    ).controls.filter(entity => entity.dirty === true); // get all the rows that have been altered

    let changes = {};

    changedRows.forEach((row: FormGroup, index: number) => {
      const rowValue = row.value;

      rowValue.cdsRecoveryRate = Utilities.percentageToNumber(
        rowValue.cdsRecoveryRate
      ).toString(); // turn 6% into 0.06

      rowValue.derivativeRecoveryRate = Utilities.percentageToNumber(
        rowValue.derivativeRecoveryRate
      ).toString(); // turn 6% into 0.06

      rowValue.spreads = rowValue.spreads.map(spread => {
        return Utilities.percentageToNumber(spread).toString(); // turn 6% into 0.06
      });

      changes['creditCurvesSave' + index] =
        this.tradesCaptureService.getUserInfoRequest({
          task: 'putItem',
          table: 'creditCurves',
          item: rowValue
        });
    });

    this.curvesToDelete.forEach((row: any, index: number) => {
      changes['creditCurvesDelete' + index] =
        this.tradesCaptureService.getUserInfoRequest({
          task: 'deleteItem',
          table: 'creditCurves',
          key: 'entity',
          identifier: row.entity
        });
    });
    this.savingCurvesStatus = 'LOADING';

    forkJoin(changes).subscribe({
      next: () => {
        changedRows.forEach((row: FormControl) => {
          row.get('saved').setValue(true);
        });
        this.creditCurvesForm.markAsPristine();
        this.curvesToDelete = [];
        this.savingCurvesStatus = 'DONE';
        this.changeRef.markForCheck();

        this.resetSavingStatus();
      },
      error: () => {
        this.savingCurvesStatus = 'ERROR';
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      }
    });
  }

  resetSavingStatus() {
    setTimeout(() => {
      this.savingCurvesStatus = '';
      this.changeRef.markForCheck();
    }, 1500);
  }

  get allowedToSave(): boolean {
    return (
      this.savingCurvesStatus !== 'LOADING' &&
      this.creditCurvesForm.valid &&
      ((!this.creditCurvesForm.pristine &&
        (this.creditCurvesForm.get('curves') as FormArray).length > 0) ||
        this.curvesToDelete.length > 0)
    );
  }

  get showCreditCurves() {
    return this.tradesCaptureService.showCreditCurves;
  }

  updateScroll(table: string) {
    setTimeout(() => {
      let e =
        table === 'curves'
          ? this.divToScrollCurves.nativeElement
          : this.divToScrollTenors.nativeElement;
      e.scrollTop = e.scrollHeight;
    }, 0);
  }

  percentRateValidator(control: AbstractControl): KeyValue | null {
    if (new RegExp('^\\d+(\\.\\d+)?%$').test(control.value)) {
      const valueOnly = control.value.substring(0, control.value.length - 1);
      if (!isNaN(valueOnly) && valueOnly >= 0 && valueOnly < 100) {
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  spreadValidator(control: AbstractControl): KeyValue | null {
    if (new RegExp('^\\d+(\\.\\d+)?%$').test(control.value)) {
      const valueOnly = control.value.substring(0, control.value.length - 1);
      if (!isNaN(valueOnly) && valueOnly >= 0 && valueOnly <= 100) {
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  // percentValidator

  modalChanged(event: boolean) {
    this.tradesCaptureService.setShowCreditCurves = event;
  }

  private updateLocalEntities(entities: Entity[]) {
    this.identifiers = this.legService.updateEntities(entities);
  }
}
